
import { Link } from 'react-router-dom';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import { useEffect } from 'react';

export default function PreciosPage() {
  const plans = [
    {
      id: 1,
      name: "Plan Básico",
      students: "Hasta 60 alumnos",
      price: "$90,000",
      features: [
        "24 módulos completos de IA",
        "Material didáctico incluido",
        "Soporte por email",
        "Acceso por 1 año",
        "Certificados de participación"
      ],
      color: "from-green-500 to-green-600",
      popular: false
    },
    {
      id: 2,
      name: "Plan Estándar",
      students: "61 a 120 alumnos",
      price: "$110,000",
      features: [
        "24 módulos completos de IA",
        "Material didáctico incluido",
        "Soporte prioritario",
        "Acceso por 1 año",
        "Certificados de participación",
        "Sesiones de Q&A mensuales"
      ],
      color: "from-blue-500 to-blue-600",
      popular: true
    },
    {
      id: 3,
      name: "Plan Avanzado",
      students: "121 a 250 alumnos",
      price: "$180,000",
      features: [
        "24 módulos completos de IA",
        "Material didáctico incluido",
        "Soporte prioritario 24/7",
        "Acceso por 1 año",
        "Certificados de participación",
        "Sesiones de Q&A mensuales",
        "Capacitación para profesores"
      ],
      color: "from-purple-500 to-purple-600",
      popular: false
    },
    {
      id: 4,
      name: "Plan Premium",
      students: "251 a 400 alumnos",
      price: "$250,000",
      features: [
        "24 módulos completos de IA",
        "Material didáctico incluido",
        "Soporte dedicado 24/7",
        "Acceso por 1 año",
        "Certificados de participación",
        "Sesiones de Q&A semanales",
        "Capacitación intensiva para profesores",
        "Personalización de contenidos"
      ],
      color: "from-red-500 to-red-600",
      popular: false
    },
    {
      id: 5,
      name: "Plan Empresarial",
      students: "401+ alumnos",
      price: "$450,000",
      features: [
        "24 módulos completos de IA",
        "Material didáctico incluido",
        "Soporte dedicado 24/7",
        "Acceso por 1 año",
        "Certificados de participación",
        "Sesiones de Q&A semanales",
        "Capacitación intensiva para profesores",
        "Personalización completa",
        "Implementación on-site",
        "Dashboard de analytics"
      ],
      color: "from-gray-800 to-gray-900",
      popular: false
    }
  ];

  useEffect(() => {
    // Schema.org JSON-LD for Pricing and Offers
    const schemaData = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "Curso de Inteligencia Artificial para Educación",
      "description": "Curso completo de 24 módulos de IA diseñado para instituciones educativas",
      "brand": {
        "@type": "Brand",
        "name": "INDEFINHUM"
      },
      "category": "Educational Course",
      "offers": plans.map(plan => ({
        "@type": "Offer",
        "name": plan.name,
        "description": `Plan para ${plan.students}`,
        "price": plan.price.replace('$', '').replace(',', ''),
        "priceCurrency": "MXN",
        "availability": "https://schema.org/InStock",
        "validFrom": "2024-01-01",
        "priceValidUntil": "2024-12-31",
        "seller": {
          "@type": "Organization",
          "name": "INDEFINHUM",
          "url": import.meta.env.VITE_SITE_URL
        },
        "itemOffered": {
          "@type": "Course",
          "name": "Curso de IA - 24 Módulos",
          "provider": {
            "@type": "Organization",
            "name": "INDEFINHUM"
          }
        }
      }))
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-400 via-blue-500 to-purple-600">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-cyan-500 via-blue-600 to-purple-700 text-white py-12 sm:py-16 md:py-20">
        <div className="container mx-auto px-4 sm:px-6 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6">
            Planes de Inversión
          </h1>
          <p className="text-base sm:text-lg md:text-xl opacity-90 mb-6 sm:mb-8 max-w-3xl mx-auto px-4">
            Elige el plan que mejor se adapte al tamaño de tu institución educativa. 
            Todos incluyen los 24 módulos completos del curso de IA.
          </p>
          <div className="bg-gradient-to-r from-cyan-400/20 to-blue-500/20 backdrop-blur-sm rounded-2xl p-4 sm:p-6 max-w-2xl mx-auto border border-cyan-300/30">
            <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">¿Por qué invertir en educación de IA?</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 text-sm sm:text-base">
              <div className="flex items-center justify-center md:justify-start">
                <i className="ri-check-line mr-2 text-green-300 flex-shrink-0"></i>
                <span>Futuro profesional asegurado</span>
              </div>
              <div className="flex items-center justify-center md:justify-start">
                <i className="ri-check-line mr-2 text-green-300 flex-shrink-0"></i>
                <span>Ventaja competitiva educativa</span>
              </div>
              <div className="flex items-center justify-center md:justify-start">
                <i className="ri-check-line mr-2 text-green-300 flex-shrink-0"></i>
                <span>ROI demostrable</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Planes de Precios */}
      <section className="py-12 sm:py-16 md:py-20">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 sm:gap-8">
            {plans.map((plan) => (
              <div 
                key={plan.id} 
                className={`relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:scale-105 border border-white/20 ${plan.popular ? 'ring-2 ring-cyan-400' : ''}`}
              >
                {plan.popular && (
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-cyan-400 to-blue-500 text-white px-3 sm:px-4 py-1 rounded-b-lg text-xs sm:text-sm font-semibold">
                    Más Popular
                  </div>
                )}
                
                <div className={`bg-gradient-to-r ${plan.color} p-4 sm:p-6 text-white`}>
                  <h3 className="text-lg sm:text-xl font-bold mb-2">{plan.name}</h3>
                  <p className="text-xs sm:text-sm opacity-90 mb-3 sm:mb-4">{plan.students}</p>
                  <div className="text-2xl sm:text-3xl font-bold mb-2">{plan.price}</div>
                  <p className="text-xs sm:text-sm opacity-90">MXN por institución</p>
                </div>
                
                <div className="p-4 sm:p-6">
                  <ul className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start text-xs sm:text-sm text-white">
                        <i className="ri-check-line text-cyan-400 mr-2 flex-shrink-0 mt-0.5"></i>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Link
                    to="/pago"
                    state={{ selectedPlan: plan }}
                    className={`w-full bg-gradient-to-r ${plan.color} text-white py-2 sm:py-3 px-3 sm:px-4 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 text-center block whitespace-nowrap cursor-pointer text-sm sm:text-base`}
                  >
                    La Quiero
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparativa Detallada */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-r from-blue-600/20 to-purple-600/20 backdrop-blur-sm">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6">
              Comparativa Detallada de Planes
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-blue-100 px-4">
              Todos los planes incluyen acceso completo al curso de 24 módulos
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl shadow-lg overflow-hidden border border-white/20 min-w-[800px]">
              <thead>
                <tr className="bg-gradient-to-r from-cyan-500/30 to-blue-600/30">
                  <th className="text-left p-3 sm:p-6 font-bold text-white text-sm sm:text-base">Características</th>
                  <th className="text-center p-3 sm:p-6 font-bold text-green-300 text-sm sm:text-base">Básico</th>
                  <th className="text-center p-3 sm:p-6 font-bold text-blue-300 text-sm sm:text-base">Estándar</th>
                  <th className="text-center p-3 sm:p-6 font-bold text-purple-300 text-sm sm:text-base">Avanzado</th>
                  <th className="text-center p-3 sm:p-6 font-bold text-red-300 text-sm sm:text-base">Premium</th>
                  <th className="text-center p-3 sm:p-6 font-bold text-gray-300 text-sm sm:text-base">Empresarial</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-white/10">
                  <td className="p-3 sm:p-6 font-semibold text-white text-sm sm:text-base">Número de estudiantes</td>
                  <td className="text-center p-3 sm:p-6 text-blue-100 text-xs sm:text-base">Hasta 60</td>
                  <td className="text-center p-3 sm:p-6 text-blue-100 text-xs sm:text-base">61-120</td>
                  <td className="text-center p-3 sm:p-6 text-blue-100 text-xs sm:text-base">121-250</td>
                  <td className="text-center p-3 sm:p-6 text-blue-100 text-xs sm:text-base">251-400</td>
                  <td className="text-center p-3 sm:p-6 text-blue-100 text-xs sm:text-base">401+</td>
                </tr>
                <tr className="border-b border-white/10 bg-white/5">
                  <td className="p-3 sm:p-6 font-semibold text-white text-sm sm:text-base">24 Módulos de IA</td>
                  <td className="text-center p-3 sm:p-6"><i className="ri-check-line text-cyan-400 text-lg sm:text-xl"></i></td>
                  <td className="text-center p-3 sm:p-6"><i className="ri-check-line text-cyan-400 text-lg sm:text-xl"></i></td>
                  <td className="text-center p-3 sm:p-6"><i className="ri-check-line text-cyan-400 text-lg sm:text-xl"></i></td>
                  <td className="text-center p-3 sm:p-6"><i className="ri-check-line text-cyan-400 text-lg sm:text-xl"></i></td>
                  <td className="text-center p-3 sm:p-6"><i className="ri-check-line text-cyan-400 text-lg sm:text-xl"></i></td>
                </tr>
                <tr className="border-b border-white/10">
                  <td className="p-3 sm:p-6 font-semibold text-white text-sm sm:text-base">Soporte técnico</td>
                  <td className="text-center p-3 sm:p-6 text-blue-100 text-xs sm:text-base">Email</td>
                  <td className="text-center p-3 sm:p-6 text-blue-100 text-xs sm:text-base">Prioritario</td>
                  <td className="text-center p-3 sm:p-6 text-blue-100 text-xs sm:text-base">24/7</td>
                  <td className="text-center p-3 sm:p-6 text-blue-100 text-xs sm:text-base">Dedicado 24/7</td>
                  <td className="text-center p-3 sm:p-6 text-blue-100 text-xs sm:text-base">Dedicado 24/7</td>
                </tr>
                <tr className="border-b border-white/10 bg-white/5">
                  <td className="p-3 sm:p-6 font-semibold text-white text-sm sm:text-base">Sesiones Q&A</td>
                  <td className="text-center p-3 sm:p-6 text-blue-100 text-xs sm:text-base">-</td>
                  <td className="text-center p-3 sm:p-6 text-blue-100 text-xs sm:text-base">Mensuales</td>
                  <td className="text-center p-3 sm:p-6 text-blue-100 text-xs sm:text-base">Mensuales</td>
                  <td className="text-center p-3 sm:p-6 text-blue-100 text-xs sm:text-base">Semanales</td>
                  <td className="text-center p-3 sm:p-6 text-blue-100 text-xs sm:text-base">Semanales</td>
                </tr>
                <tr className="border-b border-white/10">
                  <td className="p-3 sm:p-6 font-semibold text-white text-sm sm:text-base">Capacitación profesores</td>
                  <td className="text-center p-3 sm:p-6 text-blue-100 text-xs sm:text-base">-</td>
                  <td className="text-center p-3 sm:p-6 text-blue-100 text-xs sm:text-base">-</td>
                  <td className="text-center p-3 sm:p-6"><i className="ri-check-line text-cyan-400 text-lg sm:text-xl"></i></td>
                  <td className="text-center p-3 sm:p-6 text-blue-100 text-xs sm:text-base">Intensiva</td>
                  <td className="text-center p-3 sm:p-6 text-blue-100 text-xs sm:text-base">Intensiva</td>
                </tr>
                <tr className="border-b border-white/10 bg-white/5">
                  <td className="p-3 sm:p-6 font-semibold text-white text-sm sm:text-base">Personalización</td>
                  <td className="text-center p-3 sm:p-6 text-blue-100 text-xs sm:text-base">-</td>
                  <td className="text-center p-3 sm:p-6 text-blue-100 text-xs sm:text-base">-</td>
                  <td className="text-center p-3 sm:p-6 text-blue-100 text-xs sm:text-base">-</td>
                  <td className="text-center p-3 sm:p-6 text-blue-100 text-xs sm:text-base">Contenidos</td>
                  <td className="text-center p-3 sm:p-6 text-blue-100 text-xs sm:text-base">Completa</td>
                </tr>
                <tr>
                  <td className="p-3 sm:p-6 font-semibold text-white text-sm sm:text-base">Implementación</td>
                  <td className="text-center p-3 sm:p-6 text-blue-100 text-xs sm:text-base">Remota</td>
                  <td className="text-center p-3 sm:p-6 text-blue-100 text-xs sm:text-base">Remota</td>
                  <td className="text-center p-3 sm:p-6 text-blue-100 text-xs sm:text-base">Remota</td>
                  <td className="text-center p-3 sm:p-6 text-blue-100 text-xs sm:text-base">Remota</td>
                  <td className="text-center p-3 sm:p-6 text-blue-100 text-xs sm:text-base">On-site</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Testimonios */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-r from-purple-600/20 to-cyan-500/20 backdrop-blur-sm">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6">
              Lo que Dicen Nuestros Clientes
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm p-6 sm:p-8 rounded-2xl shadow-lg border border-white/20">
              <div className="flex items-center mb-4">
                <img 
                  src="https://public.readdy.ai/ai/img_res/2ae51ec13511c45b94750bf024843f6b.jpg" 
                  alt="Directora"
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover mr-3 sm:mr-4 flex-shrink-0"
                />
                <div>
                  <h4 className="font-bold text-white text-sm sm:text-base">Dra. María González</h4>
                  <p className="text-xs sm:text-sm text-blue-200">Directora, Colegio San Patricio</p>
                </div>
              </div>
              <p className="text-blue-100 italic text-sm sm:text-base">
                "El curso de IA transformó completamente nuestra metodología educativa. Los estudiantes están más motivados y preparados para el futuro."
              </p>
              <div className="flex text-yellow-400 mt-4">
                <i className="ri-star-fill text-sm sm:text-base"></i>
                <i className="ri-star-fill text-sm sm:text-base"></i>
                <i className="ri-star-fill text-sm sm:text-base"></i>
                <i className="ri-star-fill text-sm sm:text-base"></i>
                <i className="ri-star-fill text-sm sm:text-base"></i>
              </div>
            </div>

            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm p-6 sm:p-8 rounded-2xl shadow-lg border border-white/20">
              <div className="flex items-center mb-4">
                <img 
                  src="https://readdy.ai/api/search-image?query=Male%20technology%20teacher%2C%20computer%20science%20educator%2C%20professional%20instructor%20in%20modern%20classroom&width=80&height=80&seq=teacher-testimonial&orientation=squarish" 
                  alt="Profesor"
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover mr-3 sm:mr-4 flex-shrink-0"
                />
                <div>
                  <h4 className="font-bold text-white text-sm sm:text-base">Prof. Carlos Ramírez</h4>
                  <p className="text-xs sm:text-sm text-blue-200">Coordinador TI, Instituto Tecnológico</p>
                </div>
              </div>
              <p className="text-blue-100 italic text-sm sm:text-base">
                "Los 24 módulos están perfectamente estructurados. Mis estudiantes de secundaria ahora comprenden conceptos que antes parecían imposibles."
              </p>
              <div className="flex text-yellow-400 mt-4">
                <i className="ri-star-fill text-sm sm:text-base"></i>
                <i className="ri-star-fill text-sm sm:text-base"></i>
                <i className="ri-star-fill text-sm sm:text-base"></i>
                <i className="ri-star-fill text-sm sm:text-base"></i>
                <i className="ri-star-fill text-sm sm:text-base"></i>
              </div>
            </div>

            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm p-6 sm:p-8 rounded-2xl shadow-lg border border-white/20">
              <div className="flex items-center mb-4">
                <img 
                  src="https://readdy.ai/api/search-image?query=Female%20elementary%20school%20teacher%2C%20primary%20education%20instructor%2C%20caring%20educator%20with%20children&width=80&height=80&seq=elementary-teacher&orientation=squarish" 
                  alt="Maestra"
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover mr-3 sm:mr-4 flex-shrink-0"
                />
                <div>
                  <h4 className="font-bold text-white text-sm sm:text-base">Mtra. Ana López</h4>
                  <p className="text-xs sm:text-sm text-blue-200">Maestra, Escuela Primaria Benito Juárez</p>
                </div>
              </div>
              <p className="text-blue-100 italic text-sm sm:text-base">
                "Increíble cómo los niños de primaria pueden entender IA con estos métodos. El material está adaptado perfectamente para cada edad."
              </p>
              <div className="flex text-yellow-400 mt-4">
                <i className="ri-star-fill text-sm sm:text-base"></i>
                <i className="ri-star-fill text-sm sm:text-base"></i>
                <i className="ri-star-fill text-sm sm:text-base"></i>
                <i className="ri-star-fill text-sm sm:text-base"></i>
                <i className="ri-star-fill text-sm sm:text-base"></i>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-r from-blue-600/20 to-purple-600/20 backdrop-blur-sm">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6">
              Preguntas Frecuentes
            </h2>
          </div>

          <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6">
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-white/20">
              <h3 className="font-bold text-white mb-2 sm:mb-3 text-sm sm:text-base">¿Qué incluye exactamente cada plan?</h3>
              <p className="text-blue-100 text-sm sm:text-base">
                Todos los planes incluyen acceso completo a los 24 módulos del curso, material didáctico, certificados y soporte. Las diferencias están en el nivel de soporte, capacitación adicional y personalización.
              </p>
            </div>

            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-white/20">
              <h3 className="font-bold text-white mb-2 sm:mb-3 text-sm sm:text-base">¿Cómo se determina el número de estudiantes?</h3>
              <p className="text-blue-100 text-sm sm:text-base">
                El conteo se basa en el número total de estudiantes que tendrán acceso al curso durante el año lectivo, independientemente de si lo toman simultáneamente o en diferentes períodos.
              </p>
            </div>

            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-white/20">
              <h3 className="font-bold text-white mb-2 sm:mb-3 text-sm sm:text-base">¿Qué métodos de pago aceptan?</h3>
              <p className="text-blue-100 text-sm sm:text-base">
                Aceptamos transferencias bancarias SPEI, pagos con PayPal y tarjetas de débito. La transferencia bancaria tiene condiciones preferenciales.
              </p>
            </div>

            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-white/20">
              <h3 className="font-bold text-white mb-2 sm:mb-3 text-sm sm:text-base">¿Hay descuentos por pagos anuales?</h3>
              <p className="text-blue-100 text-sm sm:text-base">
                Los precios mostrados ya incluyen el descuento por pago anual. También ofrecemos planes de pago fraccionado para instituciones que lo requieran.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-r from-gray-900 via-blue-900 to-purple-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6">
            ¿Listo para Transformar tu Institución?
          </h2>
          <p className="text-base sm:text-lg md:text-xl mb-8 sm:mb-10 opacity-90 max-w-3xl mx-auto px-4">
            No esperes más. El futuro de la educación está en la IA y tus estudiantes merecen estar preparados.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center px-4">
            <Link 
              to="/modulos" 
              className="border-2 border-cyan-400 text-cyan-300 px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-cyan-400 hover:text-blue-900 transition-all duration-300 whitespace-nowrap cursor-pointer text-center"
            >
              Ver Módulos Detallados
            </Link>
            <Link
              to="/pago"
              className="bg-gradient-to-r from-cyan-400 to-blue-500 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:shadow-xl transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer text-center"
            >
              Comenzar Ahora
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
